import React from 'react';
import { templates } from '@klickly/klickly-templates';
import { Typography } from '@ui';
import { Flex } from 'antd';
import UnitItem from '../../UnitItem';
import TabHead from '../components/TabHead';
import {
    StyledUnitWrapper160x600,
    StyledUnitWrapper300x250,
    StyledUnitWrapper300x600,
    StyledUnitWrapper320x50,
    StyledUnitWrapper320x100,
    StyledUnitWrapper336x280,
    StyledUnitWrapper728x90,
    StyledUnitWrapper970x250
} from '../styled';

const SIZE_970x250 = '970x250';
const SIZE_728x90 = '728x90';
const SIZE_320x50 = '320x50';
const SIZE_320x100 = '320x100';
const SIZE_300x250 = '300x250';
const SIZE_300x600 = '300x600';
const SIZE_336x280 = '336x280';
const SIZE_160x600 = '160x600';

// Product-curated example lists — deliberately not derived from the AWA rotation, so a
// rotation change (adds/removes/reorders) never silently changes what the wizard shows.
// Spelled out as literal IDs (not generated from a range) so the exact contract stays
// greppable — product owns this list and it need not stay contiguous V1..Vn.
const CURATED_LAYOUTS_BY_SIZE = {
    [SIZE_970x250]: [
        '2402-970x250-V1',
        '2402-970x250-V2',
        '2402-970x250-V3',
        '2402-970x250-V4',
        '2402-970x250-V5',
        '2402-970x250-V6',
        '2402-970x250-V7',
        '2402-970x250-V8',
        '2402-970x250-V9'
    ],
    [SIZE_728x90]: [
        '2402-728x90-V1',
        '2402-728x90-V2',
        '2402-728x90-V3',
        '2402-728x90-V4',
        '2402-728x90-V5',
        '2402-728x90-V6'
    ],
    [SIZE_320x50]: ['2402-320x50-V1', '2402-320x50-V2', '2402-320x50-V3'],
    [SIZE_320x100]: ['2402-320x100-V1', '2402-320x100-V2', '2402-320x100-V3'],
    [SIZE_300x250]: [
        '2402-300x250-V1',
        '2402-300x250-V2',
        '2402-300x250-V3',
        '2402-300x250-V4',
        '2402-300x250-V5',
        '2402-300x250-V6'
    ],
    [SIZE_300x600]: [
        '2311-300x600-V1',
        '2311-300x600-V2',
        '2311-300x600-V3',
        '2311-300x600-V4',
        '2311-300x600-V5',
        '2311-300x600-V6',
        '2311-300x600-V7',
        '2311-300x600-V8',
        '2311-300x600-V9'
    ],
    [SIZE_336x280]: [
        '2402-336x280-V1',
        '2402-336x280-V2',
        '2402-336x280-V3',
        '2402-336x280-V4',
        '2402-336x280-V5',
        '2402-336x280-V6'
    ],
    [SIZE_160x600]: ['2402-160x600-V1', '2402-160x600-V2', '2402-160x600-V3', '2402-160x600-V4']
};

/**
 * Curated layout IDs to show as AWA design examples for a size. Filters the curated list
 * against the size's actual `LAYOUT_CONFIG`: an id that no longer resolves there is NOT "a
 * broken example" — templates 4.0.0 falls back to the size's default layout for an unknown id,
 * so leaving it in would render a silent visible duplicate. Dropping it here is the safe
 * default; the accompanying dev-only `console.warn` is what keeps that drift from shipping
 * unnoticed, by naming the size and the missing id(s) at the first wizard render.
 * @param {string} size - e.g. '970x250'; also used to resolve `templates.Unit<size>`
 * @returns {string[]} layout IDs
 */
const getCuratedExampleLayouts = (size) => {
    const layoutConfig = templates[`Unit${size}`].LAYOUT_CONFIG;
    const curatedIds = CURATED_LAYOUTS_BY_SIZE[size] ?? [];
    const resolvedIds = [];
    const missingIds = [];

    curatedIds.forEach((layoutId) => {
        if (layoutConfig[layoutId]) {
            resolvedIds.push(layoutId);
        } else {
            missingIds.push(layoutId);
        }
    });

    if (missingIds.length && process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console -- dev-only drift warning for a curated list with no test coverage
        console.warn(
            `[AdExamplesModal] ${size}: curated layout id(s) missing from templates.Unit${size}.LAYOUT_CONFIG — ${missingIds.join(
                ', '
            )}. Update CURATED_LAYOUTS_BY_SIZE.`
        );
    }

    return resolvedIds;
};

export const getAdExamplesItems = (brandedData, promotions) => {
    return [
        {
            key: SIZE_970x250,
            label: (
                <Typography.Title
                    data-e2e='campaignwizard_adexample-970x250_tab'
                    level={3}
                    align='center'
                    color='unset'
                >
                    970x250
                </Typography.Title>
            ),
            children: (
                <Flex vertical>
                    <TabHead />
                    <StyledUnitWrapper970x250>
                        {getCuratedExampleLayouts(SIZE_970x250).map((layout) => {
                            return (
                                <UnitItem
                                    key={layout}
                                    brandedData={brandedData}
                                    promotions={promotions}
                                    layout={layout}
                                    width={970}
                                    height={250}
                                />
                            );
                        })}
                    </StyledUnitWrapper970x250>
                </Flex>
            )
        },
        {
            key: SIZE_728x90,
            label: (
                <Typography.Title data-e2e='campaignwizard_adexample-728x90_tab' level={3} align='center' color='unset'>
                    728x90
                </Typography.Title>
            ),
            children: (
                <Flex vertical>
                    <TabHead />
                    <StyledUnitWrapper728x90>
                        {getCuratedExampleLayouts(SIZE_728x90).map((layout) => {
                            return (
                                <UnitItem
                                    key={layout}
                                    brandedData={brandedData}
                                    promotions={promotions}
                                    layout={layout}
                                    width={728}
                                    height={90}
                                />
                            );
                        })}
                    </StyledUnitWrapper728x90>
                </Flex>
            )
        },
        {
            key: SIZE_320x50,
            label: (
                <Typography.Title data-e2e='campaignwizard_adexample-320x50_tab' level={3} align='center' color='unset'>
                    320x50
                </Typography.Title>
            ),
            children: (
                <Flex vertical>
                    <TabHead />
                    <StyledUnitWrapper320x50>
                        {getCuratedExampleLayouts(SIZE_320x50).map((layout) => {
                            return (
                                <UnitItem
                                    key={layout}
                                    brandedData={brandedData}
                                    promotions={promotions}
                                    layout={layout}
                                    width={320}
                                    height={50}
                                />
                            );
                        })}
                    </StyledUnitWrapper320x50>
                </Flex>
            )
        },
        {
            key: SIZE_320x100,
            label: (
                <Typography.Title
                    data-e2e='campaignwizard_adexample-320x100_tab'
                    level={3}
                    align='center'
                    color='unset'
                >
                    320x100
                </Typography.Title>
            ),
            children: (
                <Flex vertical>
                    <TabHead />
                    <StyledUnitWrapper320x100>
                        {getCuratedExampleLayouts(SIZE_320x100).map((layout) => {
                            return (
                                <UnitItem
                                    key={layout}
                                    brandedData={brandedData}
                                    promotions={promotions}
                                    layout={layout}
                                    width={320}
                                    height={100}
                                />
                            );
                        })}
                    </StyledUnitWrapper320x100>
                </Flex>
            )
        },
        {
            key: SIZE_300x250,
            label: (
                <Typography.Title
                    data-e2e='campaignwizard_adexample-300x250_tab'
                    level={3}
                    align='center'
                    color='unset'
                >
                    300x250
                </Typography.Title>
            ),
            children: (
                <Flex vertical>
                    <TabHead />
                    <StyledUnitWrapper300x250>
                        {getCuratedExampleLayouts(SIZE_300x250).map((layout) => {
                            return (
                                <UnitItem
                                    key={layout}
                                    brandedData={brandedData}
                                    promotions={promotions}
                                    layout={layout}
                                    width={300}
                                    height={250}
                                />
                            );
                        })}
                    </StyledUnitWrapper300x250>
                </Flex>
            )
        },
        {
            key: SIZE_300x600,
            label: (
                <Typography.Title
                    data-e2e='campaignwizard_adexample-300x600_tab'
                    level={3}
                    align='center'
                    color='unset'
                >
                    300x600
                </Typography.Title>
            ),
            children: (
                <Flex vertical>
                    <TabHead />
                    <StyledUnitWrapper300x600>
                        {getCuratedExampleLayouts(SIZE_300x600).map((layout) => {
                            return (
                                <UnitItem
                                    key={layout}
                                    brandedData={brandedData}
                                    promotions={promotions}
                                    layout={layout}
                                />
                            );
                        })}
                    </StyledUnitWrapper300x600>
                </Flex>
            )
        },
        {
            key: SIZE_336x280,
            label: (
                <Typography.Title
                    data-e2e='campaignwizard_adexample-336x280_tab'
                    level={3}
                    align='center'
                    color='unset'
                >
                    336x280
                </Typography.Title>
            ),
            children: (
                <Flex vertical>
                    <TabHead />
                    <StyledUnitWrapper336x280>
                        {getCuratedExampleLayouts(SIZE_336x280).map((layout) => {
                            return (
                                <UnitItem
                                    key={layout}
                                    brandedData={brandedData}
                                    promotions={promotions}
                                    layout={layout}
                                    width={336}
                                    height={280}
                                />
                            );
                        })}
                    </StyledUnitWrapper336x280>
                </Flex>
            )
        },
        {
            key: SIZE_160x600,
            label: (
                <Typography.Title
                    data-e2e='campaignwizard_adexample-160x600_tab'
                    level={3}
                    align='center'
                    color='unset'
                >
                    160x600
                </Typography.Title>
            ),
            children: (
                <Flex vertical>
                    <TabHead />
                    <StyledUnitWrapper160x600>
                        {getCuratedExampleLayouts(SIZE_160x600).map((layout) => {
                            return (
                                <UnitItem
                                    key={layout}
                                    brandedData={brandedData}
                                    promotions={promotions}
                                    layout={layout}
                                    width={160}
                                />
                            );
                        })}
                    </StyledUnitWrapper160x600>
                </Flex>
            )
        }
    ];
};
